import React, { useMemo } from 'react'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import TodoTypes from '@/enums/todo-types.enum'
import IconProcess from '@assets/images/icons/todo_process.svg'
import IconAttention from '@assets/images/icons/icon_feather_eye.svg'
import IconRelease from '@assets/images/icons/todo_release.svg'
import IconSignaturePen from '@assets/images/icons/signature_pen.svg'
import useCheckModuleAccess from '@/modules/_helpers/accessCheck/useCheckModuleAccess'
import { mimeTypes, SUBSCRIPTION_MODULES } from '@/modules/base/util/constants'
import TodoTypeSelectListOption from '@/modules/todo/components/todo-form/todo-type-select/todo-type-select-list-option'
import { useAppSelector } from '@/redux/store'

const { todoList } = lang
const typeOptions = [
  {
    value: TodoTypes.PROCESS,
    label: todoList.typeLabels[TodoTypes.PROCESS],
    icon: IconProcess,
  },
  {
    value: TodoTypes.RELEASE,
    label: todoList.typeLabels[TodoTypes.RELEASE],
    icon: IconRelease,
  },
  {
    value: TodoTypes.ATTENTION,
    label: todoList.typeLabels[TodoTypes.ATTENTION],
    icon: IconAttention,
  },
  {
    value: TodoTypes.SIGNATURE,
    label: todoList.typeLabels[TodoTypes.SIGNATURE],
    icon: IconSignaturePen,
  },
]

const useStyles = makeStyles(() => ({
  typeButtonList: {
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 5,
    width: '100%',
  },
}))

interface TodoTypeSelectListProps {
  value: keyof typeof TodoTypes,
  withoutSignatureOption: boolean,
  document: object,
  onChange: (value: keyof typeof TodoTypes) => void
}

const TodoTypeSelectList = (props: TodoTypeSelectListProps) => {
  const { onChange, value: selectedType, withoutSignatureOption = false, document } = props
  const classes = useStyles()
  const isPdf = !!document && document?.mimetype === mimeTypes.MIMETYPE_PDF
  const subscriptions = useAppSelector(state => state.tenant.info.subscriptions)
  const isTaxConsultant = useMemo(() => { return subscriptions?.some(s => s.isTaxConsultant) }, [subscriptions])
  // TODO: cleanup signature subscription modules, its not clear why there are two different modules for signatures
  const planIncludesSignatures = useCheckModuleAccess(isTaxConsultant ? SUBSCRIPTION_MODULES.DOCUMENT_SIGNATURE : SUBSCRIPTION_MODULES.SIGNATURES)
  const documentSelected = !!document
  const hasAccess = (!withoutSignatureOption && planIncludesSignatures)
  const shouldRenderSignatureOption = documentSelected ? hasAccess && isPdf : hasAccess

  const handleClick = (value) => {
    onChange && onChange(value)
  }

  const options = shouldRenderSignatureOption
    ? typeOptions
    : typeOptions.filter((typeOption) => typeOption.value !== TodoTypes.SIGNATURE)

  return (
    <div className={classes.typeButtonList}>
      {options.map((option) => {
        const { value: type } = option

        return (
          <TodoTypeSelectListOption
            {...option}
            key={`typeButton-${type}`}
            onClick={(e) => {
              e.preventDefault()
              handleClick(type)
            }}
            active={selectedType === type} />
        )
      },
      )}
    </div>
  )
}

export default TodoTypeSelectList
